import PropTypes from 'prop-types';
import React from 'react';
import { createPortal } from 'react-dom';

import { getClients } from 'common-src/models/Client';

import useCustomSelector from 'src/hooks/useCustomSelector';

import { ESCALATED_TO_OPTIONS } from '../../constants';
import { buildEmptyEscalationTeamGroups, groupClientsByEscalationTeam } from '../../helpers';
import styles from './ReassignModal.module.scss';

const ReassignModal = ({
  task,
  onClose,
  onConfirm,
  isSubmitting = false,
  currentUserName = '',
}) => {
  const clients = useCustomSelector((state) => getClients(state)) || [];
  const [query, setQuery] = React.useState('');
  const [selectedClientId, setSelectedClientId] = React.useState(null);
  const [openTeams, setOpenTeams] = React.useState({});
  const currentOwnerName = `${task?.owner || ''}`.trim();
  const currentOwner = currentOwnerName.toLowerCase();
  const normalizedCurrentUserName = `${currentUserName || ''}`.trim().toLowerCase();
  const isAssigned = !!currentOwnerName && currentOwner !== 'unassigned';
  const isAssignedToCurrentUser =
    !!normalizedCurrentUserName && currentOwner === normalizedCurrentUserName;
  const modalTitle = isAssigned ? 'Reassign task' : 'Assign to';
  const modalSubtitle = isAssigned
    ? isAssignedToCurrentUser
      ? 'Ownership transfers to the new assignee. You will no longer own this task.'
      : 'Ownership transfers to the new assignee.'
    : 'Choose a person to assign this task to.';
  const primaryActionLabel = isSubmitting
    ? isAssigned
      ? 'Reassigning...'
      : 'Assigning...'
    : modalTitle;

  const clientsByTeam = React.useMemo(() => groupClientsByEscalationTeam(clients), [clients]);

  const filteredClientsByTeam = React.useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    if (!normalizedQuery) return clientsByTeam;

    const grouped = buildEmptyEscalationTeamGroups();

    Object.entries(clientsByTeam).forEach(([teamValue, teamClients]) => {
      grouped[teamValue] = teamClients.filter((item) => {
        const haystack = `${item.label} ${item.role}`.toLowerCase();
        return haystack.includes(normalizedQuery);
      });
    });

    return grouped;
  }, [clientsByTeam, query]);

  const flatFilteredClients = React.useMemo(
    () =>
      ESCALATED_TO_OPTIONS.flatMap((option) =>
        (filteredClientsByTeam[option.value] || []).map((item) => ({
          ...item,
          teamValue: option.value,
          teamLabel: option.label,
        })),
      ),
    [filteredClientsByTeam],
  );

  React.useEffect(() => {
    const nextOpenTeams = {};
    ESCALATED_TO_OPTIONS.forEach((option) => {
      nextOpenTeams[option.value] = false;
    });
    setOpenTeams(nextOpenTeams);
  }, []);

  const toggleTeam = (teamValue) => {
    setOpenTeams((prev) => ({
      ...prev,
      [teamValue]: !prev[teamValue],
    }));
  };

  const selected =
    flatFilteredClients.find((item) => item.id === selectedClientId) ||
    Object.values(clientsByTeam)
      .flat()
      .find((item) => item.id === selectedClientId);

  const isCurrentOwnerSelected = selected?.label?.toLowerCase() === currentOwner;
  const isSearching = query.trim().length > 0;

  const modalContent = (
    <div
      className={styles.modalOverlay}
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className={styles.modalCard}
        role="dialog"
        aria-modal="true"
        aria-labelledby="reassign-title"
      >
        <div className={styles.modalHeader}>
          <div>
            <div id="reassign-title" className={styles.modalTitle}>
              {modalTitle}
            </div>
            <div className={styles.modalSubtitle}>{modalSubtitle}</div>
          </div>
        </div>

        <div className={styles.modalBody}>
          <div className={styles.userPicker}>
            <div className={styles.searchWrap}>
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                className={styles.searchInput}
                placeholder="Search people..."
              />
            </div>
            <div className={styles.teamSections}>
              {isSearching ? (
                !flatFilteredClients.length ? (
                  <div className={styles.teamSectionEmpty}>No users found</div>
                ) : (
                  <div className={styles.teamSectionList}>
                    {flatFilteredClients.map((item) => {
                      const isSelected = selectedClientId === item.id;
                      const isCurrentOwner = item.label.toLowerCase() === currentOwner;

                      return (
                        <button
                          key={`${item.teamValue}-${item.id}`}
                          type="button"
                          className={`${styles.teamMemberRow} ${
                            isSelected ? styles.teamMemberRowActive : ''
                          }`}
                          onClick={() => !isCurrentOwner && setSelectedClientId(item.id)}
                          disabled={isCurrentOwner}
                        >
                          <span className={styles.teamMemberAvatar}>{item.initials}</span>

                          <span className={styles.teamMemberInfo}>
                            <span className={styles.teamMemberName}>{item.label}</span>
                            <span className={styles.teamMemberRole}>{item.role || '—'}</span>
                          </span>

                          {isCurrentOwner ? (
                            <span className={styles.currentOwnerTag}>Current</span>
                          ) : (
                            <span className={styles.teamMemberBadge}>{item.teamLabel}</span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                )
              ) : (
                ESCALATED_TO_OPTIONS.map((option) => {
                  const teamClients = filteredClientsByTeam[option.value] || [];
                  const isOpen = !!openTeams[option.value];

                  return (
                    <div key={option.value} className={styles.teamSection}>
                      <button
                        type="button"
                        className={styles.teamSectionHeader}
                        onClick={() => toggleTeam(option.value)}
                      >
                        <span className={styles.teamSectionLeft}>
                          <span
                            className={`${styles.teamSectionChevron} ${
                              isOpen ? styles.teamSectionChevronOpen : ''
                            }`}
                            aria-hidden="true"
                          >
                            ›
                          </span>
                          <span className={styles.teamSectionIcon} aria-hidden="true">
                            {option.icon}
                          </span>
                          <span className={styles.teamSectionTitle}>{option.label}</span>
                        </span>
                        <span className={styles.teamSectionCount}>{teamClients.length}</span>
                      </button>

                      {isOpen ? (
                        <div className={styles.teamSectionList}>
                          {!teamClients.length ? (
                            <div className={styles.teamSectionEmpty}>No users found</div>
                          ) : (
                            teamClients.map((item) => {
                              const isSelected = selectedClientId === item.id;
                              const isCurrentOwner = item.label.toLowerCase() === currentOwner;

                              return (
                                <button
                                  key={`${option.value}-${item.id}`}
                                  type="button"
                                  className={`${styles.teamMemberRow} ${
                                    isSelected ? styles.teamMemberRowActive : ''
                                  }`}
                                  onClick={() => !isCurrentOwner && setSelectedClientId(item.id)}
                                  disabled={isCurrentOwner}
                                >
                                  <span className={styles.teamMemberAvatar}>{item.initials}</span>

                                  <span className={styles.teamMemberInfo}>
                                    <span className={styles.teamMemberName}>{item.label}</span>
                                    <span className={styles.teamMemberRole}>
                                      {item.role || '—'}
                                    </span>
                                  </span>

                                  {isCurrentOwner ? (
                                    <span className={styles.currentOwnerTag}>Current</span>
                                  ) : (
                                    <span className={styles.teamMemberBadge}>{option.label}</span>
                                  )}
                                </button>
                              );
                            })
                          )}
                        </div>
                      ) : null}
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>

        <div className={styles.modalFooter}>
          <button type="button" onClick={onClose} className={styles.modalSecondaryAction}>
            Cancel
          </button>
          <button
            type="button"
            onClick={async () => {
              if (!selected || isCurrentOwnerSelected) return;
              const shouldClose = await onConfirm?.(selected.client);
              if (shouldClose !== false) onClose();
            }}
            className={styles.modalPrimaryAction}
            disabled={!selected || isCurrentOwnerSelected || isSubmitting}
          >
            {primaryActionLabel}
          </button>
        </div>
      </div>
    </div>
  );

  if (typeof document === 'undefined') return modalContent;

  return createPortal(modalContent, document.body);
};

ReassignModal.propTypes = {
  task: PropTypes.shape({
    title: PropTypes.string,
    assessmentType: PropTypes.string,
    assessmentVariant: PropTypes.string,
    owner: PropTypes.string,
  }),
  onClose: PropTypes.func,
  onConfirm: PropTypes.func,
  isSubmitting: PropTypes.bool,
  currentUserName: PropTypes.string,
};

export default ReassignModal;
